import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CashflowFormModal } from "../components/investments/CashflowFormModal";
import { CashflowTable } from "../components/investments/CashflowTable";
import { CountryWeightsSection } from "../components/investments/CountryWeightsSection";
import { InvestmentForm } from "../components/investments/InvestmentForm";
import { PerformanceSection } from "../components/investments/PerformanceSection";
import { PeriodSelector } from "../components/investments/PeriodSelector";
import { QuoteFormModal } from "../components/investments/QuoteFormModal";
import { QuoteHistoryChart } from "../components/investments/QuoteHistoryChart";
import { RefreshButton } from "../components/investments/RefreshButton";
import { TransactionFormModal } from "../components/investments/TransactionFormModal";
import { TransactionTable } from "../components/investments/TransactionTable";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { Modal } from "../components/ui/Modal";
import { useAccounts } from "../hooks/accounts";
import {
  useAddManualQuote,
  useDeleteQuote,
  useHardDeleteInvestment,
  useInvestmentDetail,
  useInvestmentQuotes,
  useSoftDeleteInvestment,
  useTransactions,
  useUpdateInvestment,
  useUpdateRenditebrueckeOverrides,
} from "../hooks/investments";
import { isBalanceInvestmentKind } from "../lib/investments";
import type { CreateManualQuoteInput, UpdateInvestmentInput } from "../lib/investments";
import { mapInvestmentsError } from "../lib/investments-errors";
import { RenditebrueckeCard } from "./otterfolio/RenditebrueckeCard";

function formatEur(cents: number | null): string {
  if (cents === null) return "—";
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("de-DE");
}

const REFRESH_DISABLED_KINDS = new Set(["ANLEIHE", "SONSTIGE", "P2P", "BETEILIGUNG"]);

export function InvestmentDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const period = searchParams.get("period") ?? "total";

  function setPeriod(next: string) {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (next === "total") sp.delete("period");
      else sp.set("period", next);
      return sp;
    });
  }

  const detail = useInvestmentDetail(id ?? "", period);
  const quotesQuery = useInvestmentQuotes(id ?? "", "MAX");
  const txQuery = useTransactions(id ?? "", Boolean(id));
  const accounts = useAccounts({ active: "true" });
  const update = useUpdateInvestment();
  const softDelete = useSoftDeleteInvestment();
  const hardDelete = useHardDeleteInvestment();
  const addQuote = useAddManualQuote();
  const deleteQuote = useDeleteQuote();
  const updateRenditebrueckeOverrides = useUpdateRenditebrueckeOverrides();
  const [editOpen, setEditOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [confirmHard, setConfirmHard] = useState(false);
  const [editError, setEditError] = useState<string | undefined>();
  const [addCashflowOpen, setAddCashflowOpen] = useState(false);
  const [addTxOpen, setAddTxOpen] = useState(false);

  if (detail.isLoading || !detail.data) {
    return (
      <div className="fv-card p-10 text-center" style={{ color: "var(--muted)" }}>
        …
      </div>
    );
  }
  const inv = detail.data;
  const refreshDisabled = !inv.isActive || REFRESH_DISABLED_KINDS.has(inv.kind);
  const refreshDisabledReason = !inv.isActive
    ? t("investments.refresh.disabled_inactive")
    : REFRESH_DISABLED_KINDS.has(inv.kind)
      ? t("investments.refresh.disabled_kind")
      : undefined;

  const quotes = quotesQuery.data?.quotes ?? [];
  const showChart = quotes.length >= 2;

  async function handleEditSubmit(input: UpdateInvestmentInput) {
    setEditError(undefined);
    try {
      await update.mutateAsync({ id: inv.id, input });
      setEditOpen(false);
    } catch (err) {
      const code =
        err &&
        typeof err === "object" &&
        "code" in err &&
        typeof (err as { code: unknown }).code === "string"
          ? (err as { code: string }).code
          : undefined;
      setEditError(code ? t(mapInvestmentsError(code)) : t("errors.unknown"));
    }
  }

  async function handleAddQuote(input: CreateManualQuoteInput) {
    await addQuote.mutateAsync({ id: inv.id, input });
  }

  return (
    <section className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs mb-2" style={{ color: "var(--muted)" }}>
            <Link to="/investments" className="hover:underline">
              {t("nav.investments")}
            </Link>
            <span>/</span>
            <span style={{ color: "var(--text)" }}>{inv.name}</span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold tracking-tight">{inv.name}</h1>
            <span className="mono text-sm" style={{ color: "var(--muted)" }}>
              {inv.symbol}
            </span>
            <span
              className="px-2 py-0.5 rounded-md text-[10px] font-semibold"
              style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
            >
              {t(`investments.kind.${inv.kind}`)}
            </span>
            {!inv.isActive && (
              <span
                className="px-2 py-0.5 rounded-md text-[10px] font-semibold"
                style={{ background: "var(--border-soft)", color: "var(--muted)" }}
              >
                {t("investments.detail.inactive_badge", { defaultValue: "Inaktiv" })}
              </span>
            )}
          </div>
          <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>
            {inv.currency}
            {inv.account && (
              <>
                {" · "}
                <Link to={`/accounts/${inv.account.id}`} style={{ color: "var(--accent)" }}>
                  {inv.account.name}
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="px-3 py-2 rounded-lg border text-sm font-medium"
            style={{ borderColor: "var(--border)", background: "var(--card)" }}
            onClick={() => setEditOpen(true)}
          >
            {t("investments.detail.edit")}
          </button>
          {inv.isActive ? (
            <button
              type="button"
              className="px-3 py-2 rounded-lg border text-sm font-medium"
              style={{ borderColor: "var(--border)", background: "var(--card)" }}
              onClick={() => softDelete.mutate(inv.id)}
            >
              {t("investments.detail.deactivate")}
            </button>
          ) : (
            <button
              type="button"
              className="px-3 py-2 rounded-lg border text-sm font-medium"
              style={{ borderColor: "var(--border)", background: "var(--card)" }}
              onClick={() => update.mutate({ id: inv.id, input: { isActive: true } })}
            >
              {t("investments.detail.activate")}
            </button>
          )}
        </div>
      </div>

      {/* Period Selector */}
      <PeriodSelector
        value={period}
        onChange={setPeriod}
        periodStartIso={inv.periodReturn?.periodStartIso ?? null}
        periodEndIso={inv.periodReturn?.periodEndIso ?? null}
      />

      {/* Performance */}
      {inv.periodReturn && <PerformanceSection periodReturn={inv.periodReturn} kind={inv.kind} />}

      {/* Key Stats Strip */}
      <div
        className="fv-card grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x"
        style={{ borderColor: "var(--border)" }}
      >
        <StatMini
          label={t("investments.detail.market_value", { defaultValue: "Marktwert" })}
          value={formatEur(inv.calc.marketValueEur)}
        />
        <StatMini
          label={t("investments.detail.quantity", { defaultValue: "Stueck" })}
          value={inv.quantity}
        />
        <StatMini
          label={t("investments.detail.purchase_price", { defaultValue: "Kaufkurs" })}
          value={formatEur(inv.purchasePriceCents)}
        />
        <StatMini
          label={t("investments.detail.gain_loss", { defaultValue: "G/V" })}
          value={formatEur(inv.calc.gainLossEur ?? null)}
          tone={
            inv.calc.gainLossEur === null || inv.calc.gainLossEur === undefined
              ? "muted"
              : inv.calc.gainLossEur >= 0
                ? "pos"
                : "neg"
          }
        />
      </div>

      {/* Renditebruecke (nur befuellt fuer Otterfolio-Positionen mit Fundamentals) */}
      <RenditebrueckeCard
        rb={inv.renditebruecke}
        overrides={{
          growthEstimatePercent: inv.growthEstimatePercent,
          payoutYieldPercent: inv.payoutYieldPercent,
          valuationDeltaPercent: inv.valuationDeltaPercent,
        }}
        renditebrueckeUpdatedAt={inv.renditebrueckeUpdatedAt}
        onSave={async (v) => {
          await updateRenditebrueckeOverrides.mutateAsync({ id: inv.id, input: v });
        }}
      />

      {/* Chart Section */}
      <div className="fv-card overflow-hidden">
        <div
          className="px-5 py-3 flex items-center justify-between border-b"
          style={{ borderColor: "var(--border)" }}
        >
          <h2 className="text-base font-semibold">{t("investments.detail.history")}</h2>
          <div className="flex gap-2">
            <RefreshButton
              investmentId={inv.id}
              disabled={refreshDisabled}
              disabledReason={refreshDisabledReason}
            />
            <button
              type="button"
              className="px-3 py-1.5 rounded-lg border text-xs font-medium"
              style={{ borderColor: "var(--border)", background: "var(--card)" }}
              onClick={() => setQuoteOpen(true)}
            >
              {t("investments.detail.add_quote")}
            </button>
          </div>
        </div>
        <div className="p-4">
          {quotesQuery.isLoading ? (
            <p className="text-center" style={{ color: "var(--muted)" }}>
              …
            </p>
          ) : quotes.length === 0 ? (
            <p className="text-center" style={{ color: "var(--muted)" }}>
              {t("investments.detail.no_quotes")}
            </p>
          ) : (
            showChart && (
              <QuoteHistoryChart
                quotes={quotes.map((q) => ({
                  asOf: q.asOf,
                  priceCents: q.priceCents,
                  currency: q.currency,
                }))}
                transactions={(txQuery.data?.transactions ?? []).map((tx) => ({
                  date: tx.date,
                  type: tx.type,
                  shares: tx.shares,
                  priceCents: tx.priceCents,
                  amountCents: tx.amountCents,
                  feeCents: tx.feeCents,
                  taxCents: tx.taxCents,
                  currency: tx.currency,
                }))}
                currency={inv.currency}
              />
            )
          )}
        </div>
      </div>

      {/* P2P/BETEILIGUNG Cashflow */}
      {(inv.kind === "P2P" || inv.kind === "BETEILIGUNG") && (
        <div className="fv-card overflow-hidden">
          <div
            className="px-5 py-3 flex items-center justify-between border-b"
            style={{ borderColor: "var(--border)" }}
          >
            <h2 className="text-base font-semibold">{t("investments.cashflow.section_title")}</h2>
            <button
              type="button"
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-white"
              style={{ background: "var(--accent)" }}
              onClick={() => setAddCashflowOpen(true)}
            >
              + {t("investments.cashflow.add_button")}
            </button>
          </div>
          <CashflowTable investmentId={inv.id} />
          {addCashflowOpen && (
            <CashflowFormModal investmentId={inv.id} onClose={() => setAddCashflowOpen(false)} />
          )}
        </div>
      )}

      {/* Transactions — KPI-Tiles nur mit vorhandenen Tx (sonst leere Zahlen), aber
          Tabelle + Add-Button IMMER fuer nicht-balance-Kinds, damit die allererste
          Transaktion ueberhaupt angelegt werden kann (sonst Henne-Ei-Problem bei
          frisch angelegten Otterfolio-Positionen ohne Auto-Buy-Tx). */}
      {!isBalanceInvestmentKind(inv.kind) && (
        <div className="space-y-3">
          {inv.isTransactional && (
            <div
              className="fv-card grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x"
              style={{ borderColor: "var(--border)" }}
            >
              <StatMini
                label={t("investments.transaction.kpi_realized_gain")}
                value={formatEur(inv.calc.realizedGainEur ?? null)}
                tone={
                  inv.calc.realizedGainEur === null || inv.calc.realizedGainEur === undefined
                    ? "muted"
                    : inv.calc.realizedGainEur >= 0
                      ? "pos"
                      : "neg"
                }
              />
              <StatMini
                label={t("investments.transaction.kpi_dividends_net")}
                value={formatEur(inv.calc.dividendsNetEur ?? null)}
              />
              <StatMini
                label={t("investments.transaction.kpi_total_return")}
                value={formatEur(inv.calc.totalReturnEur ?? null)}
                tone={
                  inv.calc.totalReturnEur === null || inv.calc.totalReturnEur === undefined
                    ? "muted"
                    : inv.calc.totalReturnEur >= 0
                      ? "pos"
                      : "neg"
                }
                emphasis
              />
            </div>
          )}
          <div className="fv-card overflow-hidden">
            <div
              className="px-5 py-3 flex items-center justify-between border-b"
              style={{ borderColor: "var(--border)" }}
            >
              <h2 className="text-base font-semibold">
                {t("investments.transaction.section_title")}
              </h2>
              <button
                type="button"
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-white"
                style={{ background: "var(--accent)" }}
                onClick={() => setAddTxOpen(true)}
              >
                + {t("investments.transaction.add_button")}
              </button>
            </div>
            <TransactionTable investmentId={inv.id} />
            {addTxOpen && (
              <TransactionFormModal investmentId={inv.id} onClose={() => setAddTxOpen(false)} />
            )}
          </div>
        </div>
      )}

      {/* Country Weights (nur fuer ETF + AKTIE) */}
      {(inv.kind === "ETF" || inv.kind === "AKTIE") && (
        <CountryWeightsSection investmentId={inv.id} kind={inv.kind} />
      )}

      {/* Quote history details */}
      {inv.quoteCount > 0 && (
        <details className="fv-card overflow-hidden">
          <summary
            className="cursor-pointer select-none px-5 py-3 text-sm font-medium hover:bg-[var(--row-hover)]"
            style={{ color: "var(--text)" }}
          >
            {t("investments.detail.quote_history_summary", { count: inv.quoteCount })}
          </summary>
          <div className="border-t overflow-x-auto" style={{ borderColor: "var(--border)" }}>
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-left text-xs"
                  style={{ background: "var(--border-soft)", color: "var(--muted)" }}
                >
                  <th className="px-3 py-2 font-semibold">{t("investments.quote.column.as_of")}</th>
                  <th className="px-3 py-2 text-right font-semibold">
                    {t("investments.quote.column.price")}
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    {t("investments.quote.column.currency")}
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    {t("investments.quote.column.source")}
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    {t("investments.quote.column.provider")}
                  </th>
                  <th className="px-3 py-2 w-8" />
                </tr>
              </thead>
              <tbody>
                {[...quotes]
                  .sort((a, b) => b.asOf.localeCompare(a.asOf))
                  .map((q) => (
                    <tr
                      key={q.id}
                      className="border-b"
                      style={{ borderColor: "var(--border-soft)" }}
                    >
                      <td className="px-3 py-2">{formatDateTime(q.asOf)}</td>
                      <td className="px-3 py-2 text-right num">{formatEur(q.priceCents)}</td>
                      <td className="px-3 py-2">{q.currency}</td>
                      <td className="px-3 py-2">
                        <span
                          className="px-2 py-0.5 text-xs rounded-md"
                          style={{
                            background:
                              q.source === "AUTO" ? "var(--accent-soft)" : "var(--warn-soft)",
                            color: q.source === "AUTO" ? "var(--accent)" : "var(--warn)",
                          }}
                        >
                          {t(`investments.quote.source.${q.source}`)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">{q.provider ?? "—"}</td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          className="text-sm"
                          style={{ color: "var(--neg)" }}
                          onClick={() => deleteQuote.mutate({ id: inv.id, quoteId: q.id })}
                        >
                          {t("investments.detail.delete_quote")}
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {/* Hard delete */}
      <div className="text-center pt-2">
        <button
          type="button"
          className="text-xs underline disabled:opacity-50"
          style={{ color: "var(--neg)" }}
          onClick={() => setConfirmHard(true)}
          disabled={inv.quoteCount > 0}
          title={inv.quoteCount > 0 ? t("investments.detail.hard_delete_disabled") : undefined}
        >
          {t("investments.detail.hard_delete")}
        </button>
      </div>

      {editOpen && (
        <Modal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title={t("investments.detail.edit")}
        >
          <InvestmentForm
            mode="edit"
            initial={inv}
            accounts={accounts.data?.accounts ?? []}
            onSubmit={(input) => handleEditSubmit(input as UpdateInvestmentInput)}
            submitting={update.isPending}
            serverError={editError}
          />
        </Modal>
      )}

      {quoteOpen && (
        <QuoteFormModal
          open={quoteOpen}
          defaultCurrency={inv.currency as "EUR" | "USD" | "GBP" | "CHF" | "JPY"}
          onClose={() => setQuoteOpen(false)}
          onSubmit={handleAddQuote}
        />
      )}

      {confirmHard && (
        <ConfirmDialog
          open
          title={t("investments.detail.hard_delete_title")}
          message={t("investments.detail.hard_delete_message", { name: inv.name })}
          confirmLabel={t("investments.detail.hard_delete_confirm")}
          destructive
          onConfirm={async () => {
            await hardDelete.mutateAsync(inv.id);
            setConfirmHard(false);
          }}
          onCancel={() => setConfirmHard(false)}
        />
      )}
    </section>
  );
}

// ============== StatMini helper ==============
function StatMini({
  label,
  value,
  tone,
  emphasis,
}: {
  label: string;
  value: string;
  tone?: "pos" | "neg" | "muted";
  emphasis?: boolean;
}) {
  const color =
    tone === "pos"
      ? "var(--pos)"
      : tone === "neg"
        ? "var(--neg)"
        : tone === "muted"
          ? "var(--muted)"
          : "var(--text)";
  return (
    <div className="p-4">
      <div className="fv-kpi-label mb-1">{label}</div>
      <div
        className={`${emphasis ? "text-2xl" : "text-xl"} font-bold tracking-tight num`}
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}
